import type { ReactNode } from 'react';
import { semanticColor, surfaceTheme } from '@beautyburst/tokens';
import type { SurfaceThemeName } from '@beautyburst/tokens';

export const BRAND = {
  name: 'BEAUTY BURST',
  tagline: 'Where Confidence Meets Convenience.',
  customerPromise: 'Beauty products from local stores, delivered.',
  legalEntity: 'Beauty Burst Technologies LLC',
} as const;

/**
 * Public paths to the founder-supplied logo artwork. The files live in the
 * repository-root `assets/brand` directory, which every web application serves
 * as its Vite `publicDir`, so all three portals reference one identical asset.
 * Never point these at a generated or substitute mark.
 */
export const BRAND_LOGO = {
  /** Bag artwork, wordmark and tagline. For wide headers. */
  full: '/brand/beauty-burst-logo-full.png',
  /** Bag artwork only, wordmark intact on the bag. For compact sidebars. */
  mark: '/brand/beauty-burst-logo-mark.png',
} as const;

export interface LogoProps {
  /** full = wordmark + bag mark; mark = bag mark only (small viewports). */
  variant?: 'full' | 'mark';
  /** Approved logo asset path, injected by the consuming application. */
  src?: string;
  height?: number;
}

/**
 * Renders the approved Beauty Burst logo. The approved shopping-bag artwork is
 * supplied by the founder and referenced by path: this component never
 * substitutes a generated replacement mark.
 */
export function Logo({ variant = 'full', src, height = 36 }: LogoProps) {
  const alt = variant === 'full' ? `${BRAND.name} — ${BRAND.tagline}` : BRAND.name;
  if (src === undefined) {
    return (
      <span
        data-bb-component="logo"
        data-bb-logo-variant={variant}
        data-bb-logo-pending-asset="true"
        aria-label={alt}
        role="img"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height,
          fontWeight: 800,
          letterSpacing: '0.04em',
          color: semanticColor.brandContrast,
        }}
      >
        {variant === 'full' ? BRAND.name : 'BB'}
      </span>
    );
  }
  return (
    <img
      data-bb-component="logo"
      data-bb-logo-variant={variant}
      src={src}
      alt={alt}
      height={height}
    />
  );
}

export interface SurfaceProps {
  surface: SurfaceThemeName;
  children: ReactNode;
}

/** Applies the correct canvas per surface: blush retail vs neutral operational. */
export function Surface({ surface, children }: SurfaceProps) {
  const theme = surfaceTheme[surface];
  return (
    <div
      data-bb-surface={surface}
      style={{
        background: theme.canvas,
        color: semanticColor.textPrimary,
        minHeight: '100%',
        fontFamily: "'Manrope', 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

/** Skip link required on every application shell for keyboard users. */
export function SkipToContent({ targetId = 'main' }: { targetId?: string }) {
  return (
    <a
      href={`#${targetId}`}
      data-bb-component="skip-link"
      onClick={() => document.getElementById(targetId)?.focus()}
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        background: semanticColor.brandContrast,
        color: semanticColor.textOnPlum,
        padding: '0.75rem 1rem',
        borderRadius: '0 0 0.625rem 0',
        zIndex: 600,
      }}
    >
      Skip to main content
    </a>
  );
}
